import React, { useContext, useState } from 'react';
import { AsNumber, AsSelect } from '@adserve/adserve-react-components';
import { ScrollTable, ScrollTableHead, ScrollTableBody } from '../../generic/ScrollableTable';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import { CampaignGoalEnum, DefaultCostings } from '../../../constants/Enums';

const cellStyle = {
    width: 'auto',
    height: 28,
    border: '1px solid #D7D7D7',
    backgroundColor: '#FFFFFF',
    textAlign: 'left'
};
const headerCellStyle = {
    ...cellStyle,
    textAlign: 'center'
};
const subHeaderCellStyle = {
    ...headerCellStyle,
    width: '8%'
};
const overrideHeaderCellStyle = {
    ...headerCellStyle,
    width: '13%'
};
const headerTextStyle = {
    color: '#414141',
    fontFamily: 'Roboto',
    fontSize: 15,
    fontWeight: 500,
    marginLeft: '2%'
};
const headerSubTextStyle = {
    color: '#414141',
    fontFamily: 'Roboto',
    fontSize: 11,
    fontWeight: 500
};
const bodyCellStyle = {
    ...cellStyle,
    textAlign: 'right',
    padding: '0 10px'
};
const subBodyCellStyle = {
    ...bodyCellStyle,
    width: '8%'
};
const overrideBodyCellStyle = {
    ...bodyCellStyle,
    padding: '0 35px 0 0',
    width: '13%'
};
const bodyTextStyle = {
    color: '#414141',
    fontFamily: 'Roboto',
    fontSize: 15,
    fontWeight: 500
};
const stationTextStyle = {
    color: '#414141',
    fontFamily: 'Roboto',
    fontSize: 12,
    fontWeight: 500,
    position: 'relative',
    top: '-8%'
};

const SelectedStationsTable = ({
    campaignStations,
    readOnly = false,
    setSelectedStations,
    splitOrGoal = 'Natural Split',
    setOverride,
    changeMade,
    selectedGoal
}) => {
    const { formatNumber, formatCurrency } = useContext(SystemSettingsContext);

    const removeStation = (station) => {
        if (readOnly) return;

        if (campaignStations.find((s) => s.id === station.id)) {
            const theRest = campaignStations.filter((s) => s.id !== station.id);
            setSelectedStations([...theRest]);
        }
    };

    const availableOptions = () => {
        return [
            { id: DefaultCostings.None, name: 'None' },
            { id: DefaultCostings.Cpt, name: 'CPT' },
            { id: DefaultCostings.Spot, name: 'Spot' },
            { id: DefaultCostings.PreferCpt, name: 'Prefer Cpt' },
            { id: DefaultCostings.PreferSpot, name: 'Prefer Spot' }
        ];
    };

    const TableRows = ({ stations, setOverride, changeMade, readOnly = false }) => {
        // Work with stations copy to avoid rerendering.
        // When the original campaign object gets updated this page rerenders and lose focus on edting override value.
        const [data, setData] = useState(stations);

        const handleOverrideChange = (event, station) => {
            const copy = [...data];
            const index = copy.findIndex((s) => s.id === station.id);
            copy[index].budget = event.target.value;
            copy[index].isOverriden = true;

            setData(copy);
        };

        const handleOverrideBlur = (event, station) => {
            if (typeof setOverride === 'function') {
                setOverride(event.target.value, station);
            }
        };

        const handleDefaultCostingChange = (event, station) => {
            const copy = [...data];
            const index = copy.findIndex((s) => s.id === station.id);
            copy[index].defaultCosting = event.target.value;

            setData(copy);
            changeMade('campaignStations', copy);
        };

        const tableRow = (st, i, isDummy = false) => {
            const alternateColour = i % 2 === 0 ? '#F7F7F7' : '#FFFFFF';
            return (
                <>
                    {selectedGoal.goal === CampaignGoalEnum.BUDGET || selectedGoal.goal === CampaignGoalEnum.BASE || selectedGoal.goal === CampaignGoalEnum.TARGET ? (
                        <tr key={`data${i}`}>
                            <td
                                style={{
                                    ...bodyCellStyle,
                                    padding: 0,
                                    width: 30,
                                    textAlign: 'center',
                                    verticalAlign: 'center',
                                    backgroundColor: alternateColour
                                }}
                            >
                                {!isDummy && (
                                    <svg
                                        style={{
                                            width: 18,
                                            height: 21,
                                            paddingTop: 3,
                                            cursor: `${readOnly ? 'cursor' : 'pointer'}`,
                                            fill: '#414141'
                                        }}
                                        viewBox='10 10 30 30'
                                        onClick={() => removeStation(st)}
                                    >
                                        <path d='M38.9 16c-.4 0-.8-.1-1.2-.1v-.5c0-1.7-.2-1.9-1.9-1.9h-4.5v-.8c-.1-2.6-2.2-4.5-4.6-4.4-1.3.1-2.6.1-3.8 0-1.4-.1-3.7 1.5-4 3.1-.1.7-.2 1.3-.4 2.2h-4.9c-1 0-1.3.3-1.3 1.3 0 1.1 0 1.1-1.1 1.2 0 0-.1 0-.2.1v2.3c.7 0 1.2.1 1.8.1.7-.1.8.2.8.8 0 6.1.1 12.3 0 18.4 0 1.8 2 3.7 3.6 3.7h15.7c1.8 0 3.6-2 3.6-3.7-.1-6.1 0-12.2 0-18.4v-.9h2.6V16zM23 10.8c1.2 0 2.4.1 3.5 0 1.5-.1 2.5 1.2 2.1 2.6h-7.3c-.4-1.4.4-2.6 1.7-2.6zm10.9 26.4c0 1.3-.4 1.7-1.7 1.7H17.5c-1 0-1.5-.4-1.5-1.4V18.8c0-.1 0-.1.1-.3h17.1c.7 0 .9.2.8.9-.1 5.9-.1 11.9-.1 17.8z'></path>
                                        <path d='M18.7 21h2.4v15.2h-2.4zM23.7 21h2.4v15.2h-2.4zM28.9 21h2.3v15.2h-2.3z'></path>
                                    </svg>
                                )}
                            </td>

                            <td
                                style={{
                                    ...bodyCellStyle,
                                    width: 300,
                                    textAlign: 'left',
                                    backgroundColor: alternateColour
                                }}
                            >
                                <span style={stationTextStyle}>{st.name}</span>
                            </td>

                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    width: '11%',
                                    backgroundColor: alternateColour
                                }}
                            >
                                {!isDummy && (
                                    <AsSelect
                                        id={`select-default-costing-${i}`}
                                        placeholder={window.translate('None')}
                                        disabled={readOnly}
                                        style={{
                                            width: 100,
                                            border: 0,
                                            backgroundColor: 'rgba(255,255,255,0)',
                                            fontSize: 12,
                                            fontWeight: 500,
                                            fontFamily: 'roboto',
                                            color: 'rgb(65,65,65)',
                                            padding: '2px',
                                            MozAppearance: 'none',
                                            appearance: 'none',
                                            WebKitAppearance: 'none',
                                            outline: 'none'
                                        }}
                                        disableTooltip={true}
                                        value={st.defaultCosting}
                                        values={availableOptions()}
                                        onChange={(e) => handleDefaultCostingChange(e, st)}
                                    />
                                )}
                            </td>

                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    backgroundColor: alternateColour
                                }}
                            >
                                <span style={bodyTextStyle}>
                                    {st.cpt ? formatCurrency(parseFloat(st.cpt)?.toFixed(2)) : ''}
                                </span>
                            </td>

                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    backgroundColor: alternateColour
                                }}
                            >
                                <span style={bodyTextStyle}>{st.spot ? formatCurrency(st.spot?.toFixed(2)) : ''}</span>
                            </td>
                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    backgroundColor: alternateColour
                                }}
                            >
                                <span style={bodyTextStyle}>
                                    {selectedGoal.goal === CampaignGoalEnum.TARGET ||
                                        selectedGoal.goal === CampaignGoalEnum.BASE
                                        ? st.impactsNaturalSplit
                                        : st.naturalSplit}
                                </span>
                            </td>
                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    backgroundColor: alternateColour
                                }}
                            >
                                <span style={bodyTextStyle}>{st.sellout}</span>
                            </td>
                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    backgroundColor: alternateColour
                                }}
                            >
                                <span style={bodyTextStyle}>{st.proposed}</span>
                            </td>
                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    backgroundColor: alternateColour
                                }}
                            >
                                <span style={bodyTextStyle}>{st.override}</span>
                            </td>

                            <td
                                style={{
                                    ...overrideBodyCellStyle,
                                    backgroundColor: readOnly ? alternateColour : i % 2 === 0 ? '#FFFAE5' : '#FFFDF4'
                                }}
                            >
                                {!isDummy && (
                                    <AsNumber
                                        id={`overrideBudget${i}`}
                                        inputStyle={{
                                            boxSizing: 'border-box',
                                            height: 25,
                                            outlineStyle: 'none',
                                            autoComplete: 'off',
                                            padding: '0 5px',
                                            fontSize: 14,
                                            letterSpacing: -0.3,
                                            fontWeight: 500,
                                            width: '100%',
                                            marginLeft: 7,
                                            border: 0,
                                            backgroundColor: readOnly ? alternateColour : i % 2 === 0 ? '#FFFAE5' : '#FFFDF4'
                                        }}
                                        disabled={readOnly}
                                        value={st.budget}
                                        allowFloat={true}
                                        onChange={(e) => handleOverrideChange(e, st)}
                                        formatNumber={formatNumber}
                                        formatCurrency={selectedGoal.goal === CampaignGoalEnum.BUDGET ? formatCurrency : null}
                                        onBlur={(e) => handleOverrideBlur(e, st)}
                                    />
                                )}
                            </td>
                        </tr>
                    ) : (
                        <tr key={`data${i}`}>
                            <td
                                style={{
                                    ...bodyCellStyle,
                                    padding: 0,
                                    width: 30,
                                    textAlign: 'center',
                                    verticalAlign: 'center',
                                    backgroundColor: alternateColour
                                }}
                            >
                                {!isDummy && (
                                    <svg
                                        style={{
                                            width: 18,
                                            height: 21,
                                            paddingTop: 3,
                                            cursor: `${readOnly ? 'cursor' : 'pointer'}`,
                                            fill: '#414141'
                                        }}
                                        viewBox='10 10 30 30'
                                        onClick={() => removeStation(st)}
                                    >
                                        <path d='M38.9 16c-.4 0-.8-.1-1.2-.1v-.5c0-1.7-.2-1.9-1.9-1.9h-4.5v-.8c-.1-2.6-2.2-4.5-4.6-4.4-1.3.1-2.6.1-3.8 0-1.4-.1-3.7 1.5-4 3.1-.1.7-.2 1.3-.4 2.2h-4.9c-1 0-1.3.3-1.3 1.3 0 1.1 0 1.1-1.1 1.2 0 0-.1 0-.2.1v2.3c.7 0 1.2.1 1.8.1.7-.1.8.2.8.8 0 6.1.1 12.3 0 18.4 0 1.8 2 3.7 3.6 3.7h15.7c1.8 0 3.6-2 3.6-3.7-.1-6.1 0-12.2 0-18.4v-.9h2.6V16zM23 10.8c1.2 0 2.4.1 3.5 0 1.5-.1 2.5 1.2 2.1 2.6h-7.3c-.4-1.4.4-2.6 1.7-2.6zm10.9 26.4c0 1.3-.4 1.7-1.7 1.7H17.5c-1 0-1.5-.4-1.5-1.4V18.8c0-.1 0-.1.1-.3h17.1c.7 0 .9.2.8.9-.1 5.9-.1 11.9-.1 17.8z'></path>
                                        <path d='M18.7 21h2.4v15.2h-2.4zM23.7 21h2.4v15.2h-2.4zM28.9 21h2.3v15.2h-2.3z'></path>
                                    </svg>
                                )}
                            </td>

                            <td
                                style={{
                                    ...bodyCellStyle,
                                    width: '36.5%',
                                    textAlign: 'left',
                                    backgroundColor: alternateColour
                                }}
                            >
                                <span style={stationTextStyle}>{st.name}</span>
                            </td>

                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    width: '15%',
                                    backgroundColor: alternateColour
                                }}
                            >
                                {!isDummy && (
                                    <AsSelect
                                        id={`select-default-costing-${i}`}
                                        placeholder={window.translate('None')}
                                        disabled={readOnly}
                                        style={{
                                            width: 100,
                                            border: 0,
                                            backgroundColor: 'rgba(255,255,255,0)',
                                            fontSize: 12,
                                            fontWeight: 500,
                                            fontFamily: 'roboto',
                                            color: 'rgb(65,65,65)',
                                            padding: '2px',
                                            MozAppearance: 'none',
                                            appearance: 'none',
                                            WebKitAppearance: 'none',
                                            outline: 'none'
                                        }}
                                        disableTooltip={true}
                                        value={st.defaultCosting}
                                        values={availableOptions()}
                                        onChange={(e) => handleDefaultCostingChange(e, st)}
                                    />
                                )}
                            </td>

                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    backgroundColor: alternateColour,
                                    width: '15%'
                                }}
                            >
                                <span style={bodyTextStyle}>
                                    {st.cpt ? formatCurrency(parseFloat(st.cpt)?.toFixed(2)) : ''}
                                </span>
                            </td>

                            <td
                                style={{
                                    ...subBodyCellStyle,
                                    backgroundColor: alternateColour,
                                    width: '15%'
                                }}
                            >
                                <span style={bodyTextStyle}>{st.spot ? formatCurrency(st.spot?.toFixed(2)) : ''}</span>
                            </td>
                            <td
                                style={{
                                    ...overrideBodyCellStyle,
                                    backgroundColor: alternateColour,
                                    width: '18.5%'
                                }}
                            >
                                <span style={bodyTextStyle}>{st.budget}</span>
                            </td>
                        </tr>
                    )}
                </>
            );
        };

        const ut = [];
        if (data) {
            let i = -1;
            for (const prop in data) {
                const st = data[prop];
                i++;
                ut.push(tableRow(st, i));
            }
            for (let j = i + 1; j < 20; j++) {
                ut.push(tableRow({}, j, true));
            }
        }

        return ut;
    };

    const tableHead = () => {
        return (
            <>
                {selectedGoal.goal === CampaignGoalEnum.BUDGET || selectedGoal.goal === CampaignGoalEnum.BASE || selectedGoal.goal === CampaignGoalEnum.TARGET ? (
                    <thead>
                        <tr>
                            <th style={{ ...headerCellStyle, width: 22 }}>&nbsp;</th>
                            <th style={headerCellStyle}>
                                <span style={headerTextStyle}>
                                    {window.translate('Selected Stations:')} ({campaignStations?.length ?? 0})
                                </span>
                            </th>
                            <th style={{ ...subHeaderCellStyle, width: 131 }}>
                                <span style={headerSubTextStyle}>{window.translate('Default costing')}</span>
                            </th>
                            <th style={subHeaderCellStyle}>
                                <span style={headerSubTextStyle}>{window.translate('CPT price')}</span>
                            </th>
                            <th style={subHeaderCellStyle}>
                                <span style={headerSubTextStyle}>{window.translate('Spot price')}</span>
                            </th>
                            <th style={subHeaderCellStyle}>
                                <span style={headerSubTextStyle}>{window.translate(splitOrGoal)}</span>
                            </th>
                            <th style={subHeaderCellStyle}>
                                <span style={headerSubTextStyle}>{window.translate('Sell Out %')}</span>
                            </th>
                            <th style={subHeaderCellStyle}>
                                <span style={headerSubTextStyle}>{window.translate('Proposed %')}</span>
                            </th>
                            <th style={subHeaderCellStyle}>
                                <span style={headerSubTextStyle}>{window.translate('Override %')}</span>
                            </th>
                            <th style={overrideHeaderCellStyle}>
                                <span style={headerSubTextStyle}>{selectedGoal.goal === CampaignGoalEnum.BUDGET ? window.translate('Allocated Budget') : selectedGoal.goal === CampaignGoalEnum.BASE ? window.translate('Allocated Base Impacts') : window.translate('Allocated Target Impacts')} </span>
                            </th>
                        </tr>

                    </thead>
                ) : (
                    <thead>
                        <tr>
                            <th style={{ ...headerCellStyle, width: 22 }}>&nbsp;</th>
                            <th style={{ ...headerCellStyle, width: '37%' }}>
                                <span style={headerTextStyle}>
                                    {window.translate('Selected Stations:')} ({campaignStations?.length ?? 0})
                                </span>
                            </th>
                            <th style={{ ...subHeaderCellStyle, width: '15%' }}>
                                <span style={headerSubTextStyle}>{window.translate('Default costing')}</span>
                            </th>
                            <th style={{ ...subHeaderCellStyle, width: '15%' }}>
                                <span style={headerSubTextStyle}>{window.translate('CPT price')}</span>
                            </th>
                            <th style={{ ...subHeaderCellStyle, width: '15%' }}>
                                <span style={headerSubTextStyle}>{window.translate('Spot price')}</span>
                            </th>
                            <th style={{ ...overrideHeaderCellStyle, width: '25%' }}>
                                {selectedGoal.goal === CampaignGoalEnum.TARGETREACH &&
                                    <span style={headerSubTextStyle}>
                                        {window.translate('Target Reach %')}
                                    </span>
                                }
                                {selectedGoal.goal === CampaignGoalEnum.BASEREACH &&
                                    <span style={headerSubTextStyle}>
                                        {window.translate('Base Reach %')}
                                    </span>
                                }
                                {selectedGoal.goal === CampaignGoalEnum.TARGETOTH &&
                                    <span style={headerSubTextStyle}>
                                        {window.translate('Target Weekly OTH')}
                                    </span>
                                }
                                {selectedGoal.goal === CampaignGoalEnum.BASEOTH &&
                                    <span style={headerSubTextStyle}>
                                        {window.translate('Base Weekly OTH')}
                                    </span>
                                }
                                {selectedGoal.goal === CampaignGoalEnum.OVERALLTARGETOTH &&
                                    <span style={headerSubTextStyle}>
                                        {window.translate('Target Overall OTH')}
                                    </span>
                                }
                                {selectedGoal.goal === CampaignGoalEnum.OVERALLBASEOTH &&
                                    <span style={headerSubTextStyle}>
                                        {window.translate('Base Overall OTH')}
                                    </span>
                                }
                            </th>
                        </tr>

                    </thead>
                )}
            </>
        );
    };

    // Left commented code to check totals if needed in future
    // const getTotalValue = (field) => {
    //     let valueTotal = 0;
    //     for (let i = 0; i < campaignStations?.length; i++) {
    //         valueTotal += campaignStations[i]?.[field];
    //     }
    //     return valueTotal;
    // };

    return (
        <div style={{ width: '100%' }}>
            <ScrollTable style={{ height: 638, width: 902 }} colWidths={[20, 210, 130, 130, 130, 130, 152]}>
                <ScrollTableHead style={{ height: 30 }} getData={tableHead}></ScrollTableHead>
                <ScrollTableBody style={{ height: 581 }}>
                    <TableRows
                        stations={campaignStations.sort((s1, s2) => (s1.name.toLowerCase() > s2.name.toLowerCase()) ? 1: -1)}
                        setOverride={setOverride}
                        changeMade={changeMade}
                        readOnly={readOnly}
                    ></TableRows>
                </ScrollTableBody>
            </ScrollTable>
            {/* <div>
                <div>
                    <span>Natural Split - {getTotalValue('naturalSplit')}</span>
                </div>
                <div>
                    <span>
                        Impacts Natural Split -{' '}
                        {getTotalValue('impactsNaturalSplit')}
                    </span>
                </div>
                <div>
                    <span>Sell Out - {getTotalValue('sellout')}</span>
                </div>
                <div>
                    <span>Proposed - {getTotalValue('proposed')}</span>
                </div>
                <div>
                    <span>Override - {getTotalValue('override')}</span>
                </div>
            </div> */}
        </div>
    );
};

export default SelectedStationsTable;
